import { Alert, Button, Card, Checkbox, Group, Stack, Text, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type { ProjectAISettingsResponse, ProjectDetailResponse, TicketSdAdminApiAction } from '@/types/api'

type SdAdminAutoActionConfig = {
  enabled: boolean
  intent_description: string
  triggers: string
  ask_email_message: string
  ask_telegram_id_message: string
  confirm_email_message: string
}

const SD_ADMIN_ACTIONS: Array<{
  key: TicketSdAdminApiAction
  label: string
  intent: string
  triggers: string
  askEmail: string
  askTelegram: string
  confirmEmail: string
}> = [
  {
    key: 'payment_link',
    label: 'Платежная ссылка',
    intent: 'Клиент спрашивает, где или как оплатить подписку, просит ссылку на оплату или не может оплатить.',
    triggers: 'как оплатить\nгде оплатить\nссылка на оплату\nоплатить подписку\nне могу оплатить\nпришлите ссылку на оплату',
    askEmail: 'Напишите email, на который оформлена подписка, и я отправлю ссылку на оплату.',
    askTelegram:
      'Напишите ваш Telegram ID, чтобы я смог отправить ссылку на оплату. Также можно попробовать открыть страницу оплаты в другом браузере.',
    confirmEmail: 'Отправить ссылку на оплату на email {email}?',
  },
  {
    key: 'otp_code',
    label: 'OTP код',
    intent: 'Клиент сообщает, что не приходит код, OTP, код подтверждения или код авторизации.',
    triggers: 'код не приходит\nне приходит код\nне приходит otp\notp не приходит\nкод подтверждения\nкод авторизации',
    askEmail: 'Напишите email, на который оформлена подписка, и я отправлю код.',
    askTelegram: 'Напишите ваш Telegram ID, чтобы я смог отправить код.',
    confirmEmail: 'Отправить код на email {email}?',
  },
  {
    key: 'paid_until',
    label: 'Дата окончания подписки',
    intent: 'Клиент спрашивает, до какого числа действует подписка, когда закончится оплата или активна ли подписка.',
    triggers: 'до какого числа подписка\nкогда закончится подписка\nподписка активна\nсрок подписки\nоплачено до',
    askEmail: 'Напишите email, на который оформлена подписка, и я проверю срок действия.',
    askTelegram: 'Напишите ваш Telegram ID, чтобы я смог проверить срок действия подписки.',
    confirmEmail: 'Проверить срок подписки для email {email}?',
  },
  {
    key: 'keys_count',
    label: 'Ключи',
    intent: 'Клиент спрашивает, сколько у него ключей, какие ключи есть, просит список ключей или количество подключений.',
    triggers: 'сколько ключей\nмои ключи\nсписок ключей\nколичество ключей\nключи amnesia',
    askEmail: 'Напишите email, на который оформлена подписка, и я проверю ключи.',
    askTelegram: 'Напишите ваш Telegram ID, чтобы я смог проверить ключи.',
    confirmEmail: 'Проверить ключи для email {email}?',
  },
  {
    key: 'devices_count',
    label: 'Устройства',
    intent:
      'Клиент спрашивает, сколько устройств подключено, какие устройства привязаны или сколько мобильных устройств используется.',
    triggers: 'сколько устройств\nмои устройства\nколичество устройств\nподключенные устройства\nустройства amnesia',
    askEmail: 'Напишите email, на который оформлена подписка, и я проверю устройства.',
    askTelegram: 'Напишите ваш Telegram ID, чтобы я смог проверить устройства.',
    confirmEmail: 'Проверить устройства для email {email}?',
  },
]

const DEFAULT_SD_ADMIN_AUTO_ACTIONS = Object.fromEntries(
  SD_ADMIN_ACTIONS.map((item) => [
    item.key,
    {
      enabled: false,
      intent_description: item.intent,
      triggers: item.triggers,
      ask_email_message: item.askEmail,
      ask_telegram_id_message: item.askTelegram,
      confirm_email_message: item.confirmEmail,
    },
  ]),
) as Record<TicketSdAdminApiAction, SdAdminAutoActionConfig>

function normalizeSdAdminAutoActions(raw: unknown): Record<TicketSdAdminApiAction, SdAdminAutoActionConfig> {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const out = structuredClone(DEFAULT_SD_ADMIN_AUTO_ACTIONS)
  SD_ADMIN_ACTIONS.forEach((item) => {
    const value = source[item.key]
    if (!value || typeof value !== 'object') return
    const row = value as Record<string, unknown>
    out[item.key] = {
      ...out[item.key],
      enabled: row.enabled === true,
      intent_description:
        typeof row.intent_description === 'string' && row.intent_description.trim()
          ? row.intent_description
          : out[item.key].intent_description,
      triggers: typeof row.triggers === 'string' && row.triggers.trim() ? row.triggers : out[item.key].triggers,
      ask_email_message:
        typeof row.ask_email_message === 'string' && row.ask_email_message.trim()
          ? row.ask_email_message
          : out[item.key].ask_email_message,
      ask_telegram_id_message:
        typeof row.ask_telegram_id_message === 'string' && row.ask_telegram_id_message.trim()
          ? row.ask_telegram_id_message
          : out[item.key].ask_telegram_id_message,
      confirm_email_message:
        typeof row.confirm_email_message === 'string' && row.confirm_email_message.trim()
          ? row.confirm_email_message
          : out[item.key].confirm_email_message,
    }
  })
  return out
}

type Props = {
  projectId: string
  project: ProjectDetailResponse
  settings: ProjectAISettingsResponse
  canEdit: boolean
  onSettingsUpdated: () => void
}

export function AiAdminApiActionsTab({ projectId, project, settings, canEdit, onSettingsUpdated }: Props) {
  const [saving, setSaving] = useState(false)
  const [actions, setActions] = useState<Record<TicketSdAdminApiAction, SdAdminAutoActionConfig>>(
    DEFAULT_SD_ADMIN_AUTO_ACTIONS,
  )

  useEffect(() => {
    const rawPolicy = settings.ai_policy && typeof settings.ai_policy === 'object' ? settings.ai_policy : {}
    setActions(normalizeSdAdminAutoActions((rawPolicy as Record<string, unknown>).sd_admin_auto_actions))
  }, [settings])

  const sdAdminActions =
    project.sd_admin_api_actions && typeof project.sd_admin_api_actions === 'object' ? project.sd_admin_api_actions : {}

  const sdAdminActionEnabled = (key: TicketSdAdminApiAction) => {
    const value = (sdAdminActions as Record<string, unknown>)[key]
    return (
      value === true ||
      (value && typeof value === 'object' && !Array.isArray(value) && (value as { enabled?: boolean }).enabled === true)
    )
  }

  const updateAction = (key: TicketSdAdminApiAction, patch: Partial<SdAdminAutoActionConfig>) => {
    setActions((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }))
  }

  const save = async () => {
    if (!canEdit) return
    setSaving(true)
    try {
      await api.projectAIUpdate(projectId, {
        ai_policy: {
          ...(settings.ai_policy && typeof settings.ai_policy === 'object' ? settings.ai_policy : {}),
          sd_admin_auto_actions: actions,
        },
      })
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="md">
      {!canEdit ? (
        <Alert color="gray" variant="light">
          Нет права «Управление проектами» — изменение недоступно.
        </Alert>
      ) : null}

      <Alert color="blue" variant="light">
        Мозгобот выбирает действие через LLM по описанию намерения. Выполняются только сценарии, включенные здесь и во вкладке проекта «Admin API».
      </Alert>

      <Stack gap="md">
        {SD_ADMIN_ACTIONS.map((item) => {
          const apiEnabled = sdAdminActionEnabled(item.key)
          const value = actions[item.key]
          return (
            <Card key={item.key} withBorder padding="md" radius="md">
              <Stack gap="sm">
                <Checkbox
                  label={item.label}
                  checked={value.enabled}
                  onChange={(e) => updateAction(item.key, { enabled: e.currentTarget.checked })}
                  disabled={!canEdit || !apiEnabled}
                />
                {!apiEnabled ? (
                  <Text size="xs" c="orange">
                    Сначала включите соответствующую кнопку во вкладке проекта «Admin API».
                  </Text>
                ) : null}
                <Textarea
                  label="Когда выполнять"
                  description="Описание для LLM, не список фраз. Мозгобот сам сопоставляет смысл сообщения клиента с этим сценарием."
                  value={value.intent_description}
                  onChange={(e) => updateAction(item.key, { intent_description: e.currentTarget.value })}
                  minRows={2}
                  autosize
                  disabled={!canEdit}
                />
                <Textarea
                  label="Если email не найден"
                  value={value.ask_email_message}
                  onChange={(e) => updateAction(item.key, { ask_email_message: e.currentTarget.value })}
                  minRows={2}
                  autosize
                  disabled={!canEdit}
                />
                <Textarea
                  label="Если Telegram ID не найден"
                  value={value.ask_telegram_id_message}
                  onChange={(e) => updateAction(item.key, { ask_telegram_id_message: e.currentTarget.value })}
                  minRows={2}
                  autosize
                  disabled={!canEdit}
                />
                <Textarea
                  label="Подтверждение email из карточки"
                  description="Используйте {email}. Если в карточке несколько email, они не показываются клиенту: бот попросит написать нужный email."
                  value={value.confirm_email_message}
                  onChange={(e) => updateAction(item.key, { confirm_email_message: e.currentTarget.value })}
                  minRows={2}
                  autosize
                  disabled={!canEdit}
                />
              </Stack>
            </Card>
          )
        })}
      </Stack>

      <Group justify="flex-start">
        <Button loading={saving} disabled={!canEdit} onClick={() => void save()}>
          Сохранить автодействия
        </Button>
      </Group>
    </Stack>
  )
}
